import type { AptosWallet } from '@aptos-labs/wallet-standard';
import { getAptosWallets } from '@aptos-labs/wallet-standard';
import {
  getDefaultClient,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';

import { registerAptosNetworkProviderBuilder } from '../../registerAptosNetworkProviderBuilder';
import { registerAptosStandardWallet } from '../utils/registerAptosStandardWallet';

export const APTOS_WALLET_STANDARD_EXTENSION_KEY = 'aptosWalletStandard';

/**
 * Adds the Aptos Wallet Standard extension to the Dynamic client.
 *
 * This extension enables integration with wallets that implement the Aptos Wallet Standard (AIP-62),
 * automatically detecting and registering compatible wallets for use with the Dynamic SDK.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 *
 * @returns A function that can be used to remove Aptos Wallet Standard register event listener that was added.
 */
export const addAptosWalletStandardExtension = (
  client = getDefaultClient()
): VoidFunction => {
  if (
    hasExtension({ extensionKey: APTOS_WALLET_STANDARD_EXTENSION_KEY }, client)
  ) {
    return () => {
      // noop
    };
  }

  registerExtension(
    { extensionKey: APTOS_WALLET_STANDARD_EXTENSION_KEY },
    client
  );

  registerAptosNetworkProviderBuilder(client);

  const { aptosWallets, on } = getAptosWallets();

  // Listen for new wallet registrations/announcements
  const removeListener = on('register', (wallet) => {
    registerAptosStandardWallet(wallet as AptosWallet, client);
  });

  // Register all wallets that are already registered/announced in the browser
  for (const wallet of aptosWallets) {
    registerAptosStandardWallet(wallet, client);
  }

  return removeListener;
};
